import { type Dispatch, type SetStateAction, useMemo } from "react"

import { t } from "@tanstack/query-core/build/legacy/hydration-Cr-4Kky1"
import { useTranslation } from "react-i18next"
import { Button, Header, Message, Modal, Table } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback"

import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips"
import BaseChip from "components/lib/Chips/BaseChip"
import { AssessmentRoundLabel } from "components/lib/UI/AssessmentRoundLabel"
import util from "utils/utils"

import type { LocalAssignments } from "./AssignmentDashboard"

type AssessmentRound = OpenAPI.Schemas["IdeaTemplate"]["assessments"]["rounds"][number]

type UserInspectModalProps = {
  inspectedIdea: OpenAPI.Schemas["Idea"]
  setInspectedIdea: Dispatch<SetStateAction<OpenAPI.Schemas["Idea"] | null>>
  localAssignments: LocalAssignments
  setLocalUserAssignments: Dispatch<SetStateAction<LocalAssignments>>
  setLocalEmailAssignments: Dispatch<SetStateAction<LocalAssignments>>
  unassignAssessor: (ideaId: string, assessorId: string, assessmentRoundId: string) => void
  assessmentRoundMap: Record<string, AssessmentRound>
}

export const IdeaInspectModal = ({
  inspectedIdea,
  setInspectedIdea,
  localAssignments,
  setLocalUserAssignments,
  setLocalEmailAssignments,
  unassignAssessor,
  assessmentRoundMap,
}: UserInspectModalProps) => {
  const { t } = useTranslation()
  const ideaId = inspectedIdea?._id

  const assessmentsByUser = useMemo(() => {
    if (!inspectedIdea?.assessments) {
      return {}
    }
    const assessmentsMap: Record<string, OpenAPI.Schemas["Idea"]["assessments"][number][]> = {}
    for (const assessment of inspectedIdea.assessments) {
      if (!assessmentsMap[assessment.user]) {
        assessmentsMap[assessment.user] = []
      }
      assessmentsMap[assessment.user].push(assessment)
    }
    return assessmentsMap
  }, [inspectedIdea?.assessments])

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!inspectedIdea}
      onClose={() => setInspectedIdea(null)}
    >
      {inspectedIdea ? (
        <>
          <Modal.Header>{t("common:capitalise", { key: "generic.idea" })} assessments</Modal.Header>

          <Modal.Content scrolling>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                gap: 6,
                paddingRight: 12,
              }}
            >
              <ImageWithFallback
                avatar
                style={{
                  minWidth: 45,
                  width: 45,
                  height: 35,
                  borderRadius: 4,
                  objectFit: "cover",
                }}
                src={util.ideaCoverImage(inspectedIdea, "200x200")}
                fallbackSrc={util.ideaCoverImage()}
              />
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: "bold",
                  }}
                >
                  {inspectedIdea.name}
                </div>
              </div>
            </div>
            <Header as="h4">Draft assessments</Header>
            {!util.isEmpty(localAssignments) ? (
              <>
                <Message info>
                  These assignments are currently only in draft. Before the users is notified the assignments must first
                  be finalised and saved.
                </Message>
                <Table>
                  <Table.Body>
                    {Object.entries(localAssignments || {})
                      .filter(([userId, assignment]) => assignment.ideas?.[ideaId]?.assessmentRoundIds?.length > 0)
                      .map(([userId, assignment]) => (
                        <Table.Row key={userId}>
                          <Table.Cell collapsing>
                            {assignment.user.isEmailInvitee ? (
                              <BaseChip
                                icon="mail"
                                title={assignment.user.email}
                                compact
                                content="This will create a pending invitation, the user has not yet signed up to SimplyDo."
                              />
                            ) : (
                              <UserChip user={assignment.user.ownerAssessor} compact />
                            )}
                          </Table.Cell>
                          <Table.Cell collapsing textAlign="right">
                            {assignment.ideas?.[ideaId]?.assessmentRoundIds?.map((assessmentRoundId) => {
                              const round = assessmentRoundMap[assessmentRoundId]
                              return round ? (
                                <AssessmentRoundLabel
                                  key={round.id}
                                  assessmentRound={round}
                                  onDelete={() => {
                                    const setLocalAssignments = assignment.user.isEmailInvitee
                                      ? setLocalEmailAssignments
                                      : setLocalUserAssignments
                                    setLocalAssignments((prevState) => {
                                      const newState = { ...prevState }
                                      newState[userId] = {
                                        ...newState[userId],
                                        ideas: {
                                          ...newState[userId].ideas,
                                          [ideaId]: {
                                            ...newState[userId].ideas[ideaId],
                                            assessmentRoundIds: newState[userId].ideas[
                                              ideaId
                                            ].assessmentRoundIds.filter((id) => id !== round.id),
                                          },
                                        },
                                      }
                                      const newRounds = newState?.[userId]?.ideas?.[ideaId].assessmentRoundIds
                                      if (newRounds.length === 0) {
                                        delete newState?.[userId]?.ideas?.[ideaId]
                                      }
                                      if (Object.keys(newState?.[userId]?.ideas || {}).length === 0) {
                                        delete newState?.[userId]
                                      }
                                      return newState
                                    })
                                  }}
                                />
                              ) : null
                            })}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                </Table>
              </>
            ) : (
              <Message>No draft assignments.</Message>
            )}
            <Header as="h4">Active assessments</Header>
            {!util.isEmpty(inspectedIdea.assessments) ? (
              <Table>
                <Table.Body>
                  {Object.values(assessmentsByUser).map((assessments) => (
                    <Table.Row key={assessments[0].user}>
                      <Table.Cell collapsing>
                        <UserChip user={assessments[0].ownerUser} compact />
                      </Table.Cell>
                      <Table.Cell collapsing textAlign="right">
                        {assessments.map((assessment) => {
                          const round = assessmentRoundMap[assessment.assessmentRoundId || "legacy"]
                          return round ? (
                            <AssessmentRoundLabel
                              key={round.id}
                              assessmentRound={round}
                              onDelete={() => {
                                unassignAssessor(ideaId, assessment.user, round.id)
                              }}
                            />
                          ) : null
                        })}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Message>No active assessments.</Message>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setInspectedIdea(null)}>Close</Button>
          </Modal.Actions>
        </>
      ) : null}
    </Modal>
  )
}
